import React from 'react';
import MsDyn365 from '@msdyn365-commerce/core';
import { createPortal } from 'react-dom';

type ExpandMode = 'height' | 'width' | 'both';

type ExpandableEllipsisProps = {
    children: React.ReactNode;
    className?: string;
    overlayClassName?: string;

    expandMode?: ExpandMode;

    minWidth?: number;
    maxWidth?: number;
    minHeight?: number;
    maxHeight?: number;
};

export function ExpandableEllipsis({
    children,
    className,
    overlayClassName,
    expandMode = 'height',
    minWidth,
    maxWidth,
    minHeight,
    maxHeight
}: ExpandableEllipsisProps) {
    const referenceRef = React.useRef<HTMLSpanElement>(null);

    const [isOverflowing, setIsOverflowing] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [mounted, setMounted] = React.useState(false);

    const contentRef = React.useRef<HTMLDivElement>(null);

    const [contentHeight, setContentHeight] = React.useState(0);
    const [contentWidth, setContentWidth] = React.useState(0);

    const [rect, setRect] = React.useState<DOMRect>();

    const updateRect = React.useCallback(() => {
        if (!referenceRef.current) return;

        const newRect = referenceRef.current.getBoundingClientRect();
        newRect.width = referenceRef.current.parentElement?.getBoundingClientRect().width ?? newRect.width;
        setRect(newRect);

        // Update position of the overlay ahead of React rendering cycle to lag
        if (contentRef.current?.parentElement) {
            contentRef.current.parentElement.style.top = `${referenceRef.current.getBoundingClientRect().top}px`;
            contentRef.current.parentElement.style.left = `${referenceRef.current.getBoundingClientRect().left}px`;
        }
    }, []);

    React.useLayoutEffect(() => {
        if (typeof ResizeObserver === 'undefined') return;

        const checkOverflow = () => {
            const textOverflowTarget = referenceRef.current?.querySelector('.text-overflow-target');

            if (!textOverflowTarget) return;

            setIsOverflowing(
                textOverflowTarget.scrollWidth > textOverflowTarget.clientWidth ||
                textOverflowTarget.scrollHeight > textOverflowTarget.clientHeight
            );
        };

        checkOverflow();

        const observer = new ResizeObserver(() => checkOverflow());
        const textOverflowTarget = referenceRef.current?.querySelector('.text-overflow-target');

        if (textOverflowTarget) {
            observer.observe(textOverflowTarget);
        }

        // Update rect on scroll as position is fixed
        window.addEventListener('scroll', updateRect, true);

        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', updateRect, true);
        };
    }, []);

    React.useLayoutEffect(() => {
        if (!mounted || !contentRef.current) {
            return;
        }

        setContentHeight(contentRef.current.scrollHeight);
        setContentWidth(contentRef.current.scrollWidth);
    }, [mounted, children]);

    React.useEffect(() => {
        if (open) return;

        const timeout = setTimeout(() => setMounted(false), 200);

        return () => clearTimeout(timeout);
    }, [open]);

    React.useEffect(() => {
        if (!open || typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(() => updateRect());

        if (referenceRef.current) {
            observer.observe(referenceRef.current);
        }

        if (contentRef.current) {
            observer.observe(contentRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [open, updateRect]);

    const overlayStyle = React.useMemo<React.CSSProperties>(() => {
        if (!rect) return {};

        const style: React.CSSProperties = {
            position: 'fixed',
            top: rect.top,
            left: rect.left,
            zIndex: 1000
        };

        switch (expandMode) {
            case 'height':
                style.width = rect.width;
                style.maxWidth = rect.width;
                style.minWidth = Math.max(rect.width, minWidth ?? 0);
                break;

            case 'width':
                style.minWidth = Math.max(rect.width, minWidth ?? 0);

                if (maxWidth !== undefined) {
                    style.maxWidth = maxWidth;
                }
                break;

            case 'both':
                if (minWidth !== undefined) {
                    style.minWidth = minWidth;
                }

                if (maxWidth !== undefined) {
                    style.maxWidth = maxWidth;
                }
                break;
        }

        if (minHeight !== undefined) {
            style.minHeight = minHeight;
        }

        if (maxHeight !== undefined) {
            style.maxHeight = maxHeight;
            style.overflowY = 'auto';
        }

        return style;
    }, [
        rect,
        expandMode,
        minWidth,
        maxWidth,
        minHeight,
        maxHeight
    ]);

    return (
        <>
            <span
                ref={referenceRef}
                className={className}
                tabIndex={0}
                data-overlay-open={open}
                data-overlay-mounted={mounted}
                onMouseEnter={() => {
                    updateRect();
                    setMounted(true);

                    requestAnimationFrame(() => {
                        if (!isOverflowing) return;
                        setOpen(true);
                    });
                }}
                onMouseLeave={() => setOpen(false)}
                onFocus={() => {
                    updateRect();
                    setMounted(true);

                    requestAnimationFrame(() => {
                        if (!isOverflowing) return;
                        setOpen(true);
                    });
                }}
                onBlur={() => setOpen(false)}
            >
                {children}
            </span>

            {isOverflowing &&
                mounted &&
                rect &&
                MsDyn365.isBrowser &&
                createPortal(
                    <div
                        className={overlayClassName}
                        style={{
                            ...overlayStyle,
                            overflow: 'hidden',
                            maxHeight: open
                                ? contentHeight
                                : rect.height,
                            maxWidth: open
                                ? contentWidth
                                : rect.width,
                            opacity: open ? 1 : 0,
                            transition: 'max-width 200ms ease, max-height 200ms ease, opacity 200ms ease'
                        }}
                        onMouseEnter={() => {
                            if (!isOverflowing) return;
                            setOpen(true);
                        }}
                        onMouseLeave={() => setOpen(false)}
                    >
                        <div ref={contentRef}>
                            {children}
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
}